import axiosInstance from '../../../axiosInstance'
import apiEndpoints from '../../../api'

// Get All Invoices with pagination and filters
export const getInvoicesReq = (action) => {
    const searchQuery = action?.data || {};
    const params = {
        page: searchQuery.page || 1,
        limit: searchQuery.limit || 10,
    };

    // Add optional filters
    if (searchQuery.search) {
        params.search = searchQuery.search;
    }
    if (searchQuery.society_id) {
        params.society_id = searchQuery.society_id;
    }
    if (searchQuery.invoice_type_id) {
        params.invoice_type_id = searchQuery.invoice_type_id;
    }
    if (searchQuery.wing_id) {
        params.wing_id = searchQuery.wing_id;
    }
    if (searchQuery.flat_id) {
        params.flat_id = searchQuery.flat_id;
    }
    if (searchQuery.document_type) {
        params.document_type = searchQuery.document_type;
    }
    if (searchQuery.status) {
        params.status = searchQuery.status;
    }
    if (searchQuery.from_date) {
        params.from_date = searchQuery.from_date;
    }
    if (searchQuery.to_date) {
        params.to_date = searchQuery.to_date;
    }
    if (searchQuery.is_active !== undefined) {
        params.is_active = searchQuery.is_active;
    }
    // 0 is a valid value ("No"), so this cannot be a truthy check
    if (searchQuery.is_carried_forward !== undefined) {
        params.is_carried_forward = searchQuery.is_carried_forward;
    }

    return axiosInstance.get(apiEndpoints.GET_INVOICES_API, { params });
}

// Get Invoice Details by ID
export const getInvoiceDetailsReq = (action) => {
    const invoiceId = action?.data?.id || '';
    const url = apiEndpoints.GET_INVOICE_DETAILS_API.replace(':id', invoiceId);
    return axiosInstance.get(url);
}

// Get Invoice Statistics
export const getInvoiceStatisticsReq = (action) => {
    const params = action?.data || {};
    return axiosInstance.get(apiEndpoints.GET_INVOICE_STATISTICS_API, { params });
}

// Get Invoices by Flat
export const getInvoicesByFlatReq = (action) => {
    const flatId = action?.data?.flat_id || '';
    const url = apiEndpoints.GET_INVOICES_BY_FLAT_API.replace(':flat_id', flatId);
    const params = {};
    if (action?.data?.include_void !== undefined) params.include_void = action.data.include_void;
    if (action?.data?.invoice_type_id) params.invoice_type_id = action.data.invoice_type_id;
    return axiosInstance.get(url, { params });
}

// Get Unpaid Invoices by Flat
export const getUnpaidInvoicesByFlatReq = (action) => {
    const flatId = action?.data?.flat_id || '';
    const url = apiEndpoints.GET_UNPAID_INVOICES_BY_FLAT_API.replace(':flat_id', flatId);
    return axiosInstance.get(url);
}

// Create Invoice
export const createInvoiceReq = (action) => {
    let payload = { ...action?.data } || {};
    delete payload.onSuccess;
    return axiosInstance.post(apiEndpoints.CREATE_INVOICE_API, payload);
}

// Update Invoice
export const updateInvoiceReq = (action) => {
    const invoiceId = action?.data?.id || '';
    let payload = { ...action?.data } || {};
    delete payload.id;
    delete payload.onSuccess;
    const url = apiEndpoints.UPDATE_INVOICE_API.replace(':id', invoiceId);
    return axiosInstance.put(url, payload);
}

// Update Draft Invoice (recalculates charges, GST and round off server-side)
export const updateDraftInvoiceReq = (action) => {
    const invoiceId = action?.data?.id || '';
    let payload = { ...action?.data } || {};
    delete payload.id;
    delete payload.onSuccess;
    const url = apiEndpoints.UPDATE_DRAFT_INVOICE_API.replace(':id', invoiceId);
    return axiosInstance.put(url, payload);
}

// Delete Invoice (Soft Delete)
export const deleteInvoiceReq = (action) => {
    const invoiceId = action?.data?.id || '';
    const url = apiEndpoints.DELETE_INVOICE_API.replace(':id', invoiceId);
    return axiosInstance.delete(url);
}

// Void Invoice
export const voidInvoiceReq = (action) => {
    const invoiceId = action?.data?.id || '';
    const url = apiEndpoints.VOID_INVOICE_API.replace(':id', invoiceId);
    const payload = {};
    if (action?.data?.reason) {
        payload.reason = action.data.reason;
    }
    return axiosInstance.post(url, payload);
}

// Post Invoice (Draft -> Posted)
export const postInvoiceReq = (action) => {
    const invoiceId = action?.data?.id || '';
    const url = apiEndpoints.POST_INVOICE_API.replace(':id', invoiceId);
    return axiosInstance.post(url);
}

// Bulk Get Invoice Details
export const bulkInvoiceDetailsReq = (action) => {
    const payload = { invoice_ids: action?.data?.invoice_ids || [] };
    return axiosInstance.post(apiEndpoints.BULK_INVOICE_DETAILS_API, payload);
}

// Bulk Post Invoices (Draft -> Posted)
export const bulkPostInvoicesReq = (action) => {
    const payload = { invoice_ids: action?.data?.invoice_ids || [] };
    return axiosInstance.post(apiEndpoints.BULK_POST_INVOICES_API, payload);
}

// Bulk Delete Invoices (Soft Delete)
export const bulkDeleteInvoicesReq = (action) => {
    const payload = { invoice_ids: action?.data?.invoice_ids || [] };
    return axiosInstance.post(apiEndpoints.BULK_DELETE_INVOICES_API, payload);
}

// Bulk Preview Invoices (per-flat tariff heads + eligibility, before drafting)
export const bulkPreviewInvoicesReq = (action) => {
    let payload = { ...action?.data } || {};
    delete payload.onSuccess;
    return axiosInstance.post(apiEndpoints.BULK_PREVIEW_INVOICES_API, payload);
}

// Bulk Create Invoices
export const bulkCreateInvoicesReq = (action) => {
    let payload = { ...action?.data } || {};
    delete payload.onSuccess;
    return axiosInstance.post(apiEndpoints.BULK_CREATE_INVOICES_API, payload);
}

// Get Arrears by Flat
export const getArrearsByFlatReq = (action) => {
    const flatId = action?.data?.flat_id || '';
    const url = apiEndpoints.GET_ARREARS_BY_FLAT_API.replace(':flat_id', flatId);
    const params = {
        society_id: action?.data?.society_id,
        invoice_type_id: action?.data?.invoice_type_id,
        period_start: action?.data?.period_start,
        period_end: action?.data?.period_end,
        calculation_date: action?.data?.calculation_date,
        interest_type: action?.data?.interest_type || 'simple',
        include_debit_note_arrear: action?.data?.include_debit_note_arrear,
        include_credit_note: action?.data?.include_credit_note,
        exclude_invoice_id: action?.data?.exclude_invoice_id,
    };
    return axiosInstance.get(url, { params });
}
